import { Component, inject, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SocialMonitoring } from 'src/app/services/social-monitoring/social-monitoring';
import { SocialMonitoringInquiryResponseDto } from '@/app/common/general-types/social-screening.dto';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { VideoPreview } from '@/app/pages/manage-brief-tabs-monitor/atoms/video-preview/video-preview';
import { CarouselPreview } from '@/app/pages/manage-brief-tabs-monitor/atoms/carousel-preview/carousel-preview';
import { ImageService } from '@/app/services/image/image.service';
import { FormatService } from '@/app/common/format/format.service';
import { SuperSocialService } from '@/app/services/super-social/super-social.service';
import { MonitoringLatestPostResultsDto } from '@/app/common/general-types/super-social.dto';
import { CreatorMonitorAddAsset } from '../creator-monitor-add-asset/creator-monitor-add-asset';


@Component({
  selector: 'app-creator-monitor-inquiry',
  imports: [LoadingInlineComponent, VideoPreview, CarouselPreview, CreatorMonitorAddAsset],
  templateUrl: './creator-monitor-inquiry.html',
  styleUrl: './creator-monitor-inquiry.scss',
})
export class CreatorMonitorInquiry implements OnInit {

  // Monitor Inquiry ID
  inquiryId = signal<string | undefined>(undefined);

  creatorInquiry = signal<SocialMonitoringInquiryResponseDto | undefined>(undefined);
  performanceResults = signal<MonitoringLatestPostResultsDto[] | undefined>(undefined);

  placeholderImage = signal<string>('../../../../../assets/images/placeholder.jpeg');

  // True while a reconcile request is in flight (drives the button loading state)
  reconciling = signal<boolean>(false);

  /** Services */
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private socialMonitoringService = inject(SocialMonitoring);
  private toastNotificationService = inject(ToastNotificationService);
  public imageService = inject(ImageService);
  public formatService = inject(FormatService);
  public superSocialService = inject(SuperSocialService);
  /** Services - END */

  ngOnInit(): void {
    console.log('⭐️ ngOnInit() --> ');
    this.inquiryId.set(this.route.snapshot.paramMap.get('inquiryId') || undefined);
    if (this.inquiryId()) {
      this.initCreatorMonitorInquiry();
    }
  }

  initCreatorMonitorInquiry() {
    console.log('⭐️ initCreatorMonitorInquiry() --> ');
    /**
     * get the inquiry details, including creator and montioring brief details
     */
    if (this.inquiryId()) {
      this.socialMonitoringService.getInquiryById(this.inquiryId()!).subscribe((inquiry) => {
        console.log('⭐️ getInquiryById() --> ', inquiry);
        this.creatorInquiry.set(inquiry);

        this.getPostIds();
      });
    }
  }

  getPostIds() {
    console.log('⭐️ getPostIds() --> ');
    let postIds: string[] = [];
    if (this.creatorInquiry()?.publishedContent) {
      postIds = this.creatorInquiry()?.publishedContent.map((content) => content.socialPostId) ?? [];
    }
    console.log('⭐️ postIds --> ', postIds);
    this.getSuperSocialResults(postIds);
  }

  onAssetsAttached(): void {
    this.initCreatorMonitorInquiry();
  }

  getSuperSocialResults(postIds: string[]) {
    this.superSocialService.getCompleteLatestResultsForPostsFree(postIds).subscribe({
      next: (results: any) => {
        console.log('⭐️ performance results --> ', results);
        if (results && Object.keys(results).length > 0) {
          this.performanceResults.set(results);
        } else {
        }
      },
      error: (error: any) => {
        console.error('error getting super social results:', error);
      }
    });
  }

  getViews(video: any) {
    if (this.performanceResults() && this.performanceResults()![video.socialPostId]) {
      return this.performanceResults()![video.socialPostId].views;
    }
    return 0;
  }

  getLikes(video: any) {
    if (this.performanceResults() && this.performanceResults()![video.socialPostId]) {
      return this.performanceResults()![video.socialPostId].likes;
    }
    return 0;
  }
  
  getComments(video: any) {
    if (this.performanceResults() && this.performanceResults()![video.socialPostId]) {
      return this.performanceResults()![video.socialPostId].comments;
    }
    return 0;
  }

  getShares(video: any) {
    if (this.performanceResults() && this.performanceResults()![video.socialPostId]) {
      return this.performanceResults()![video.socialPostId].shares;
    }
    return 0;
  }
  
  getEngagementRate(video: any) {
    if (this.performanceResults() && this.performanceResults()![video.socialPostId]) {
      return this.performanceResults()![video.socialPostId].engagementRate;
    }
    return 0;
  }

  async reconcileContent() {
    const id = this.inquiryId();
    if (!id || this.reconciling()) {
      return;
    }
    this.reconciling.set(true);
    try {
      const updated = await firstValueFrom(
        this.socialMonitoringService.reconcileInquiryContent(id),
      );
      this.creatorInquiry.set(updated);
      void this.toastNotificationService.alert({
        title: 'Content reconciled',
        message: `Linked ${updated?.publishedContent?.length ?? 0} content item(s).`,
        type: 'success',
        icon: 'success',
      });
    } catch {
      void this.toastNotificationService.alert({
        title: 'Reconcile failed',
        message: 'Could not reconcile content. Please try again.',
        type: 'error',
        icon: 'error',
      });
    } finally {
      this.reconciling.set(false);
    }
  }

  getHandle(content: any) {
    if (content.socialPlatform == 'TikTok') {
      return `@${content.Handles[0].tiktok}`;
    } else if (content.socialPlatform == 'Instagram') {
      return `@${content.Handles[0].instagram}`;
    } else {
      return 'Unknown';
    }
  }

  backToManageBrief(): void {
    const briefId =
      this.creatorInquiry()?.socialMonitoringBriefId ??
      (history.state as { monitoringBriefId?: string } | null)?.monitoringBriefId;

    if (briefId) {
      void this.router.navigate(['/manage-brief-monitor', briefId, 'creators']);
      return;
    }
    this.location.back();
  }
}
